"use client";
import { Box, Button, Stack, styled, TextField } from "@mui/material";
import { useState } from "react";
import { z } from "zod";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { cn } from "@onair/utils/cn";
import { useSubscribeEmailMutation } from "@onair/operations";
import { SubscriptionEmailType } from "@onair/repositories";
import DialogSubscribeSuccess from "./dialog-subscribe-success";

interface SubScribeFormProps {
	className?: string;
}

const SubscribeEmailSchema = z.object({
	email: z
		.string()
		.trim()
		.min(1, { message: "Vui lòng nhập địa chỉ email." })
		.email("Email Không hợp lệ."),
});

type SubscribeEmailFormType = z.infer<typeof SubscribeEmailSchema>;

const SubScribeForm: React.FC<SubScribeFormProps> = ({ className }) => {
	const [showDialogSuccess, setShowDialogSuccess] = useState(false);
	const { control, handleSubmit, reset } = useForm<SubscribeEmailFormType>({
		defaultValues: { email: "" },
		resolver: zodResolver(SubscribeEmailSchema),
	});
	const { mutate: registerEmail, isPending } = useSubscribeEmailMutation();

	const onSubmit: SubmitHandler<SubscribeEmailFormType> = ({ email }) => {
		registerEmail(
			{ email, type: SubscriptionEmailType.LD_MKT },
			{
				onSuccess: () => {
					setShowDialogSuccess(true);
				},
				onSettled() {
					reset();
				},
			},
		);
	};

	return (
		<Box component="div" className={cn("subscription-form", className)}>
			<Stack
				component="div"
				direction="row"
				spacing={1}
				className="w-full border border-white rounded-xl p-2"
			>
				<Controller
					control={control}
					name="email"
					render={({ field, fieldState: { error } }) => (
						<TextFieldNoBorder
							{...field}
							placeholder="Nhập email của bạn"
							size="small"
							error={!!error}
							helperText={error?.message}
							className="no-border flex-1"
						/>
					)}
				/>
				<Button
					className="bg-white text-black"
					onClick={handleSubmit(onSubmit)}
					disabled={isPending}
				>
					{isPending ? "Đang gửi..." : "Gửi"}
				</Button>
			</Stack>
			<DialogSubscribeSuccess
				open={showDialogSuccess}
				onClose={() => setShowDialogSuccess(false)}
			/>
		</Box>
	);
};
export default SubScribeForm;
const TextFieldNoBorder = styled(TextField)`
	&.MuiFormControl-root {
		.MuiInputBase-input {
			border: none;
			padding-left: 4px;
		}
		fieldset {
			border: none;
		}
		.MuiInputBase-root {
			border: none;
			&.Mui-focused {
				border-color: transparent;
			}
		}
		&.Mui-error {
			border-color: transparent !important;
		}
		input {
			color: white;
			&::placeholder {
				color: white;
			}
		}
		.MuiFormHelperText-root {
			margin: 0;
			position: absolute;
			bottom: -4px;
			left: 4px;
			font-size: 10px;
		}
		/* Change autocomplete styles in WebKit */
		input:-webkit-autofill,
		input:-webkit-autofill:hover,
		input:-webkit-autofill:focus,
		textarea:-webkit-autofill,
		textarea:-webkit-autofill:hover,
		textarea:-webkit-autofill:focus,
		select:-webkit-autofill,
		select:-webkit-autofill:hover,
		select:-webkit-autofill:focus {
			border: none;
			-webkit-text-fill-color: black;
			-webkit-box-shadow: 0 0 0px 1000px #fff inset;
			transition: background-color 5000s ease-in-out 0s;
			background-color: white;
		}
	}
`;
